import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from "@mui/material/Tooltip";
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';

// Rétegenként: mely mezőkben keressünk (ILIKE) + opcionális sortField
const LAYER_SEARCH_CONFIG = {
  "allum_project:hr_settlements_1785": {
    fields: ["p_t001", "p_t002", "p_t003", "p_t004", "p_t005"],
    sortField: "p_t001"
  },
  "allum_project:hr_settlements_1828": {
    fields: ["nev_1913", "akoz_hr", "telname1", "telname", "Current_name", "Settlement_name"],
    sortField: "nev_1913"
  },
  "allum_project:hr_settlements_1910": {
    fields: ["polknev_1", "p_2", "p_151"],
    sortField: "polknev_1"
  },
};

// Dev/prod GeoServer base – dev-ben a CRA proxy intézi (setupProxy.js)
const isDevHost = typeof window !== 'undefined' && ['localhost','127.0.0.1'].includes(window.location.hostname);
const GS_BASE = isDevHost ? '/geoserver' : 'http://193.225.239.5/geoserver';
const GEOSERVER_WFS_OWS = `${GS_BASE}/allum_project/ows`;

const PAGE_SIZE = 5;

function buildCql(term, fields) {
  const esc = term.replace(/'/g, "''");
  return `(${fields.map(f => `${f} ILIKE '%${esc}%'`).join(" OR ")})`;
}

function labelForFeature(f, tryFields) {
  const p = f.properties || {};
  for (const k of (tryFields || [])) {
    if (p[k]) return String(p[k]);
  }
  if (p.name) return String(p.name);
  return f.id || "(no name)";
}

function cmpFeatures(a, b, tryFields) {
  const la = labelForFeature(a, tryFields);
  const lb = labelForFeature(b, tryFields);
  const s = la.localeCompare(lb, 'hu', { sensitivity: 'base' });
  if (s !== 0) return s;
  const ga = Number(a.properties?.gid ?? 0);
  const gb = Number(b.properties?.gid ?? 0);
  return ga - gb;
}

function getLeafletMap() {
  return (typeof window !== "undefined" && window.__leafletMap) ? window.__leafletMap : null;
}

// 🔸 ÚJ: egységes törlőfüggvény a kereső kijelöléshez
function clearSearchHighlight() {
  const map = getLeafletMap();
  if (map && window.__searchHighlight) {
    try { map.removeLayer(window.__searchHighlight); } catch {}
    window.__searchHighlight = null;
  }
}

async function fetchText(url) {
  const res = await fetch(url, { headers: { 'Accept': 'application/json, application/xml, text/plain' } });
  const text = await res.text();
  return { ok: res.ok, status: res.status, ct: (res.headers.get('content-type')||''), text };
}

function parseJsonSafe(text) {
  try { return JSON.parse(text); } catch { return null; }
}

function parseNumberMatchedFromXml(xmlText) {
  // tries to extract numberMatched="123" or numberOfFeatures="123"
  const m1 = xmlText.match(/numberMatched\s*=\s*\"(\d+)\"/i);
  if (m1) return parseInt(m1[1], 10);
  const m2 = xmlText.match(/numberOfFeatures\s*=\s*\"(\d+)\"/i);
  if (m2) return parseInt(m2[1], 10);
  return null;
}

function matchedFieldsForFeature(f, fields, term) {
  if (!f || !f.properties || !fields || !term) return [];
  const t = String(term).toLowerCase();
  const out = [];
  for (const k of fields) {
    const v = f.properties[k];
    if (v === null || v === undefined) continue;
    if (String(v).toLowerCase().includes(t)) out.push(k);
  }
  return out;
}

export default function SearchPanel() {
  // Reduxból kérjük az aktív réteget – így nem kell propban átadni
  const activeLayer = useSelector(s => s.MapReducer.activeLayer);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [hits, setHits] = useState(0);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);

  const cfg = useMemo(() => LAYER_SEARCH_CONFIG[activeLayer] || null, [activeLayer]);
  const inputRef = useRef(null);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  // 🔸 Bármely térképkattintásra töröljük a kereső kijelölést – akkor is, ha a map később jön létre
  useEffect(() => {
    let active = true;
    let detach = null;

    const attach = (m) => {
      const onMapClick = () => clearSearchHighlight();
      m.on('click', onMapClick);
      return () => m.off('click', onMapClick);
    };

    const tryAttach = () => {
      const m = getLeafletMap();
      if (m) {
        detach = attach(m);
        return true;
      }
      return false;
    };

    // próbáljuk meg azonnal
    if (!tryAttach()) {
      // ha még nincs, várunk rá kicsit (prod buildben a sorrend eltérhet)
      const id = setInterval(() => {
        if (!active) { clearInterval(id); return; }
        if (tryAttach()) clearInterval(id);
      }, 200);
      // takarítás: ha még fut az interval
      detach = () => clearInterval(id);
    }

    return () => { active = false; if (detach) detach(); };
  }, []);
  
  // Rétegváltáskor törlünk mindent
  useEffect(() => {
    setRows([]);
    setHits(0);
    setPage(0);
    setQ("");
    clearSearchHighlight(); // 🔸 ÚJ: biztos, ami biztos
  }, [activeLayer]);

  const runSearch = async (term, pageIndex = 0) => {
    if (!cfg || !cfg.fields?.length || !activeLayer || !term?.trim()) {
      setRows([]); setHits(0); setPage(0); return;
    }
    setLoading(true);
    try {
      const cql = buildCql(term.trim(), cfg.fields);
      const sortField = cfg.sortField || (cfg.fields?.[0] || 'name');

      // 1) HITS (WFS 2.0.0) – lehet XML vagy JSON
      let total = 0;
      try {
        const hitsParams = new URLSearchParams({
          service: "WFS",
          version: "2.0.0",
          request: "GetFeature",
          typeNames: activeLayer,
          resultType: "hits",
          cql_filter: cql,
        });
        const rH = await fetchText(`${GEOSERVER_WFS_OWS}?${hitsParams.toString()}`);
        const jH = parseJsonSafe(rH.text);
        if (jH && typeof jH.totalFeatures === "number") total = jH.totalFeatures;
        if (!total && jH && typeof jH.numberMatched === "number") total = jH.numberMatched;
        if (!total && rH.text) {
          const nm = parseNumberMatchedFromXml(rH.text);
          if (typeof nm === "number") total = nm;
        }
      } catch (_) { /* ignore hits error */ }

      // 2) Oldal lekérése – WFS 2.0.0 JSON + sortBy
      const startIndex = pageIndex * PAGE_SIZE;
      const p20 = new URLSearchParams({
        service: "WFS",
        version: "2.0.0",
        request: "GetFeature",
        typeNames: activeLayer,
        srsName: "EPSG:4326",
        outputFormat: "application/json",
        count: String(PAGE_SIZE),
        startIndex: String(startIndex),
        cql_filter: cql,
      });
      let r20 = await fetchText(`${GEOSERVER_WFS_OWS}?${p20.toString()}`);
      let j20 = parseJsonSafe(r20.text);

      // Ha nem JSON (pl. ServiceException XML), próbáljunk WFS 1.0.0 fallbacket
      if (!j20 || !Array.isArray(j20.features)) {
        // 1.0.0 – nincs standard paging; lekérünk max 200-at és kliens oldalon szeleteljük
        const p10 = new URLSearchParams({
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName: activeLayer,
          srsName: "EPSG:4326",
          outputFormat: "application/json",
          maxFeatures: "200",
          cql_filter: cql,
        });
        const r10 = await fetchText(`${GEOSERVER_WFS_OWS}?${p10.toString()}`);
        const j10 = parseJsonSafe(r10.text);
        const feats = Array.isArray(j10?.features) ? j10.features : [];
        feats.sort((a, b) => cmpFeatures(a, b, cfg?.fields));
        const slice = feats.slice(startIndex, startIndex + PAGE_SIZE);
        setRows(slice);
        setPage(pageIndex);
        setHits(total || feats.length);
      } else {
        // Biztos ami biztos: kliens oldali stabil rendezés is
        const feats = j20.features.slice().sort((a, b) => cmpFeatures(a, b, cfg?.fields));
        setRows(feats);
        setPage(pageIndex);
        setHits(total || (startIndex + feats.length + (feats.length === PAGE_SIZE ? 1 : 0)));
      }
      if (!j20 || !Array.isArray(j20.features)) {
        console.warn("[search] WFS 2.0 feature request failed", {
          status: r20.status,
          contentType: r20.ct,
          response: r20.text.slice(0, 1000),
        });
      }
    } catch (e) {
      console.error("[search] error", e);
      setRows([]); setHits(0); setPage(0);
    } finally {
      setLoading(false);
    }
  };

  const goPage = (p) => {
    if (p < 0) return;
    const maxPage = Math.max(0, Math.ceil(hits / PAGE_SIZE) - 1);
    if (p > maxPage) return;
    runSearch(q, p);
  };

  const zoomToFeature = (f) => {
    const map = getLeafletMap();
    if (!map || !f || !f.geometry) return;

    // előző highlight törlése
    clearSearchHighlight(); // 🔸 Módosítva: központi törlő

    const layer = L.geoJSON(f, { style: { color: "#ff0000", weight: 2, fillOpacity: 0.1 } });
    layer.addTo(map);
    window.__searchHighlight = layer;

    try {
      const b = layer.getBounds();
      if (b && b.isValid && b.isValid()) {
        // bal oldali dokkolt panel miatt bal oldalon nagy a padding
        map.fitBounds(b, { padding: [20, 420, 20, 20], maxZoom: 12 });
      }
    } catch {}
  };

  const totalPages = Math.max(1, Math.ceil(hits / PAGE_SIZE));
  const disabledMsg = !cfg ? "Search is not available for this layer" : "";

  return (
    <>
      {!open && (
        <Tooltip title="Open settlement search panel">
        <IconButton
          style={{color: 'gray'}}
          onClick={() => setOpen(true)}
          aria-label="open search"
        >
          <SearchIcon />
        </IconButton>
        </Tooltip>
      )}

      {open && (
        <Paper elevation={10} style={{padding: 10, paddingLeft: 16, paddingRight:16, maxHeight: 'calc(100vh - 90px)', minWidth: 'calc(100vw / 3)', maxWidth: 'calc(100vw - 30px)', overflowY: 'auto', borderRadius:10, backgroundColor:'#f0f0f0'}}>
          {/* 🔸 Bezáráskor a kereső kijelölés is törlődik */}
          <IconButton style={{color:'gray', float:'right', marginRight:-8, marginTop:-8}} onClick={()=>{ setOpen(false);}} aria-label="close">
            <CloseIcon style={{width:20}} />
          </IconButton>
          <div style={{ display: 'flex', alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #eee' }}>
            <h2>Settlement search</h2>
            <div style={{ marginLeft: 'auto' }} />
          </div>

          {/* Keresőmező + gomb */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #eee' }}>
            <TextField style={{backgroundColor: '#ffffff'}}
              inputRef={inputRef}
              size="small"
              fullWidth
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(q, 0); }}
              placeholder={disabledMsg || 'Type a settlement name…'}
              disabled={!cfg}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => runSearch(q, 0)}
              disabled={!cfg || !q.trim() || loading}
            >
              Search
            </Button>
          </div>

          {/* Aktív réteg + mezők */}
          <div style={{ padding: '6px 10px', fontSize: 12, color: '#666', borderBottom: '1px solid #eee' }}>
            Layer: <strong>{activeLayer || '-'}</strong> {cfg ? `• Fields: ${cfg.fields.join(', ')}` : ''}
          </div>

          {/* Eredménylista */}
          <div style={{ overflow: 'auto', padding: '4px 0', maxHeight: 'calc(75vh - 140px)' }}>
            {loading && <div style={{ padding: 12 }}>Searching…</div>}
            {!loading && rows.length === 0 && q.trim() && <div style={{ padding: 12 }}>No results.</div>}

            {!loading && rows.map((f) => {
              const mfs = matchedFieldsForFeature(f, cfg?.fields, q);
              return (
                <div
                  key={f.id || JSON.stringify(f.properties)}
                  onClick={() => zoomToFeature(f)}
                  style={{
                    padding: '10px 12px', borderBottom: '1px solid #f0f0f0', backgroundColor: '#ffffff',
                    cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center'
                  }}
                  title="Pan + zoom to this result"
                >
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <div style={{ fontWeight: 500, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {labelForFeature(f, cfg?.fields)}
                    </div>
                    {mfs.length > 0 && (
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        found in field(s): {mfs.join(', ')}
                      </div>
                    )}
                  </div>
                  <span style={{ color: '#888', flex: '0 0 auto' }}>›</span>
                </div>
              );
            })}
          </div>

          {/* Lapozó */}
          <div style={{ padding: 8, borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontSize: 12, color: '#666' }}>
              Total: {hits} • Page: {rows.length ? page + 1 : 0}/{Math.max(1, Math.ceil(hits / PAGE_SIZE))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="contained" size="small" onClick={() => goPage(page - 1)} disabled={loading || page <= 0}>◀</Button>
              <Button variant="contained" size="small" onClick={() => goPage(page + 1)} disabled={loading || (page + 1) >= Math.max(1, Math.ceil(hits / PAGE_SIZE))}>▶</Button>
            </div>
          </div>
        </Paper>
      )}
    </>
  );
}
